import React, { useState, useEffect } from 'react';

/**
 * DateInput Component
 * Custom date input that displays and accepts dd/mm/yyyy format
 *
 * Usage:
 * <DateInput
 *   value="2000-05-15"  // yyyy-mm-dd format from backend
 *   onChange={(value) => setFormData({...formData, date_of_birth: value})}
 *   name="date_of_birth"
 *   className="..."
 *   error={errors.date_of_birth}
 * />
 */
const DateInput = ({
  value,
  onChange,
  name,
  className = '',
  placeholder = 'DD/MM/YYYY',
  error = '',
  disabled = false,
  required = false
}) => {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    // Convert yyyy-mm-dd to dd/mm/yyyy for display
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        setDisplayValue(`${day}/${month}/${year}`);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    let input = e.target.value;

    // Remove all non-digit characters
    const digitsOnly = input.replace(/\D/g, '');

    // Format as dd/mm/yyyy while typing
    let formatted = digitsOnly;
    if (digitsOnly.length >= 2) {
      formatted = digitsOnly.slice(0, 2);
      if (digitsOnly.length >= 3) {
        formatted += '/' + digitsOnly.slice(2, 4);
        if (digitsOnly.length >= 5) {
          formatted += '/' + digitsOnly.slice(4, 8);
        }
      }
    }

    setDisplayValue(formatted);

    // Convert to yyyy-mm-dd when complete
    if (digitsOnly.length === 8) {
      const day = digitsOnly.slice(0, 2);
      const month = digitsOnly.slice(2, 4);
      const year = digitsOnly.slice(4, 8);

      // Validate date
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === parseInt(year) &&
        date.getMonth() === parseInt(month) - 1 &&
        date.getDate() === parseInt(day)
      ) {
        // Valid date - send yyyy-mm-dd to parent
        onChange(`${year}-${month}-${day}`);
      }
    } else if (digitsOnly.length === 0) {
      // Clear the value
      onChange('');
    }
  };

  const handleBlur = () => {
    // Validate complete date on blur
    if (displayValue && displayValue.length === 10) {
      const parts = displayValue.split('/');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];

        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() === parseInt(year) &&
          date.getMonth() === parseInt(month) - 1 &&
          date.getDate() === parseInt(day)
        ) {
          // Valid date - ensure it's set
          onChange(`${year}-${month}-${day}`);
        } else {
          // Invalid date - clear
          setDisplayValue('');
          onChange('');
        }
      }
    }
  };

  return (
    <div>
      <input
        type="text"
        name={name}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        maxLength={10}
        inputMode="numeric"
      />
    </div>
  );
};

export default DateInput;
